import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Heart, Sparkles, Target, Users } from 'lucide-react';

function About() {
  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      <section className="py-16 px-6 sm:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
              About Learn with Zeenat
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Empowering primary teachers with AI-powered, curriculum-aligned resources 
              to help every child learn and thrive.
            </p>
          </div>

          {/* Story Section */}
          <div className="bg-white rounded-3xl border-2 border-slate-200 shadow-lg p-8 sm:p-12 mb-12">
            <div className="flex items-center gap-3 mb-6">
              <Heart className="w-8 h-8 text-red-500" />
              <h2 className="text-3xl font-bold text-slate-900">Our Story</h2>
            </div>
            <div className="space-y-4 text-slate-700 leading-relaxed">
              <p>
                Learn with Zeenat was created by Zeenat Devjani, an experienced primary school teacher 
                who understands the daily challenges teachers face: limited time, scarce resources, 
                and the need for quality, curriculum-aligned materials.
              </p>
              <p>
                After years in the classroom, Zeenat recognized that teachers spend countless hours 
                creating worksheets, lesson plans, and assessments—time that could be better spent 
                actually teaching and connecting with students.
              </p>
              <p>
                That's why Learn with Zeenat was born: to give teachers back their time while ensuring 
                every child has access to high-quality, engaging learning materials.
              </p>
            </div>
          </div>

          {/* Mission & Values */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl border-2 border-blue-200 p-6">
              <div className="bg-blue-600 rounded-xl p-3 inline-block mb-4">
                <Target className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Our Mission</h3>
              <p className="text-slate-700">
                To help teachers save time and help every child learn through AI-powered, 
                classroom-tested educational resources.
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-2xl border-2 border-green-200 p-6">
              <div className="bg-green-600 rounded-xl p-3 inline-block mb-4">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">What We Offer</h3>
              <p className="text-slate-700">
                Curriculum-aligned worksheets, AI-powered generators, lesson plans, and teaching tips—all 
                designed by teachers, for teachers.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border-2 border-purple-200 p-6">
              <div className="bg-purple-600 rounded-xl p-3 inline-block mb-4">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Who We Serve</h3>
              <p className="text-slate-700">
                Primary teachers (Year 2-6) who want to spend less time preparing and more time 
                making a difference in their students' lives.
              </p>
            </div>
          </div>

          {/* Features Section */}
          <div className="bg-white rounded-3xl border-2 border-slate-200 shadow-lg p-8 sm:p-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">What Makes Us Different</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <div className="bg-blue-100 rounded-lg p-2 flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 mb-1">AI-Powered Tools</h4>
                  <p className="text-slate-600 text-sm">
                    Generate custom worksheets, tests, and lesson plans in seconds
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="bg-purple-100 rounded-lg p-2 flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 mb-1">Classroom-Tested</h4>
                  <p className="text-slate-600 text-sm">
                    All resources are created and tested by experienced teachers
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="bg-green-100 rounded-lg p-2 flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 mb-1">Curriculum-Aligned</h4>
                  <p className="text-slate-600 text-sm">
                    Resources match Year 2-6 curriculum standards
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="bg-yellow-100 rounded-lg p-2 flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 mb-1">Budget-Friendly</h4>
                  <p className="text-slate-600 text-sm">
                    Affordable pricing starting free, with premium options from $10/month
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact CTA */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-8 sm:p-12 text-center text-white mt-12">
            <h2 className="text-3xl font-bold mb-4">Get in Touch</h2>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              Have questions or feedback? We'd love to hear from you!
            </p>
            <a
              href="mailto:admin@learnwithzeenat.com"
              className="inline-block bg-white text-blue-600 hover:bg-blue-50 rounded-xl px-8 py-3 font-bold transition-all shadow-lg"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default About;
